import { defineStore } from "pinia";
import {
    FetchState,
    GenericBarChartDataPoint,
    GenericGraphPoint,
    HistoricalPerformanceFrequency,
    InstrumentPerformanceData,
    InstrumentPerformanceGraphTimePeriod,
    InstrumentPerformanceReturnData,
    InstrumentPerformanceTimePeriod,
    MorningstarInstrumentPerformanceGraphRequestArgs,
    MorningstarInstrumentPerformanceState
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import moment from "moment";
import * as drivewealthService from "@/shared/api/drivewealthService";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";

const getLabelForTrailingTimePeriod = (timePeriod: InstrumentPerformanceTimePeriod): string => {
    switch (timePeriod) {
        case InstrumentPerformanceTimePeriod.ClosePrice:
            return "Close Price";
        case InstrumentPerformanceTimePeriod.Months0:
            return "1 Day Return";
        case InstrumentPerformanceTimePeriod.Months1:
            return "1 Month Return";
        case InstrumentPerformanceTimePeriod.Months3:
            return "3 Months Return";
        case InstrumentPerformanceTimePeriod.Months6:
            return "6 Months Return";
        case InstrumentPerformanceTimePeriod.Months12:
            return "1 Year Return";
        case InstrumentPerformanceTimePeriod.Months36:
            return "3 Years Return";
        case InstrumentPerformanceTimePeriod.Months60:
            return "5 Years Return";
        case InstrumentPerformanceTimePeriod.Months120:
            return "10 Years Return";
        default:
            return "MM/dd/yyyy";
    }
};

const formatTrailingBarChartDataPoint = (dataPoint: InstrumentPerformanceReturnData): GenericBarChartDataPoint => {
    return {
        date: moment(dataPoint.date).format("DD-MMM-YY"),
        value: dataPoint.value,
        label: getLabelForTrailingTimePeriod(dataPoint.timePeriod)
    };
};

const formatHistoricalBarChartDataPoint = (dataPoint: InstrumentPerformanceReturnData,
                                           frequency: HistoricalPerformanceFrequency): GenericBarChartDataPoint => {
    return {
        date: moment(dataPoint.date).format("DD-MMM-YY"),
        value: dataPoint.value,
        label: frequency === HistoricalPerformanceFrequency.Yearly ?
            `${moment(dataPoint.date).year()}` :
            `Q${moment(dataPoint.date).quarter()} ${moment(dataPoint.date).year()}`,
    };
};

export const initialState: MorningstarInstrumentPerformanceState = {
    selectedTimePeriod: InstrumentPerformanceGraphTimePeriod.Weeks1,
};

const useInstrumentPerformanceDataSubStore = defineStore("morningstar_InstrumentPerformanceData", () => {
    return templatedFetchKeyCollectionStore<InstrumentPerformanceData, InstrumentPerformanceData>(
        drivewealthService.getInstrumentPerformanceData,
    );
});

const useInstrumentPerformanceGraphSubStore = defineStore("morningstar_InstrumentPerformanceGraph", () => {
    return templatedFetchKeyCollectionStore<GenericGraphPoint[], GenericGraphPoint[]>(
        drivewealthService.getInstrumentPerformanceGraphData,
    );
});

const instrumentPerformanceItemKeyFormatter = (args: MorningstarInstrumentPerformanceGraphRequestArgs): string => {
    return `${args.stockSymbol}_${args.timePeriod}`;
};

export const useInstrumentPerformanceDataStore = defineStore("instrumentPerformanceData", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("instrumentPerformanceData"),
    },
    state: (): MorningstarInstrumentPerformanceState => (_.cloneDeep(initialState)),
    getters: {
        instrumentPerformanceDataSubStore: () => useInstrumentPerformanceDataSubStore(),
        instrumentPerformanceGraphSubStore: () => useInstrumentPerformanceGraphSubStore(),
        instrumentPerformanceLoaded (): (symbol: string) => boolean {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.fetchStates[symbol];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        instrumentPerformanceLoading (): (symbol: string) => boolean {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.fetchStates[symbol];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loading;
                }

                return false;
            };
        },
        instrumentPerformanceData (): (symbol: string) => InstrumentPerformanceData {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.items[symbol];

                if (performanceLookup) {
                    return performanceLookup;
                }

                return false;
            };
        },
        fundPerformanceGraphLoaded (): (args: MorningstarInstrumentPerformanceGraphRequestArgs) => boolean {
            return (args: MorningstarInstrumentPerformanceGraphRequestArgs) => {
                const itemKey = instrumentPerformanceItemKeyFormatter(args);
                const lookup = this.instrumentPerformanceGraphSubStore.fetchStates[itemKey];

                if (lookup) {
                    return lookup === FetchState.Loaded;
                }

                return false;
            };
        },
        fundPerformanceGraphPoints (): (args: MorningstarInstrumentPerformanceGraphRequestArgs) => GenericGraphPoint[] {
            return (args: MorningstarInstrumentPerformanceGraphRequestArgs) => {
                const itemKey = instrumentPerformanceItemKeyFormatter(args);
                const lookup = this.instrumentPerformanceGraphSubStore.items[itemKey];

                if (lookup) {
                    return lookup;
                }

                return [];
            };
        },
        previousDayClosePrice (): (symbol: string) => number {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup) {
                    const data = performanceLookup.trailingPerformances;
                    const selectedData = _.find(data, { returnType: "GbPostTax" });

                    if (!selectedData) {
                        return 0;
                    }

                    const yesterdayReturn = _.find(selectedData.return, x => x.timePeriod === InstrumentPerformanceTimePeriod.ClosePrice);

                    return yesterdayReturn.value;
                }

                return 0;
            };
        },
        trailingPerformancesBarChartData (): (symbol: string) => GenericBarChartDataPoint[] {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup) {
                    const data = performanceLookup.trailingPerformances;
                    const selectedData = _.find(data, { returnType: "GbPostTax" });

                    if (!selectedData) {
                        return [];
                    }

                    const returns = _.filter(selectedData.return, x => x.timePeriod !== InstrumentPerformanceTimePeriod.ClosePrice);

                    return _.map(returns, item => formatTrailingBarChartDataPoint(item));
                }

                return [];
            };
        },
        historicalPerformancesBarChartData (): (symbol: string, frequency: HistoricalPerformanceFrequency) => GenericBarChartDataPoint[] {
            return (symbol: string, frequency: HistoricalPerformanceFrequency) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup) {
                    const data = performanceLookup.historicalPerformanceSeries;
                    const selectedData = _.find(data, { frequency, returnType: "GbPostTax" });

                    if (!selectedData) {
                        return [];
                    }

                    return _.map(selectedData.return, item => {
                        return formatHistoricalBarChartDataPoint(item, selectedData.frequency);
                    });
                }

                return [];
            };
        }
    },
    actions: {
        setSelectedTimePeriod (timePeriod: InstrumentPerformanceGraphTimePeriod) {
            this.selectedTimePeriod = timePeriod;
        },
        async getInstrumentPerformanceGraphData (
            args: MorningstarInstrumentPerformanceGraphRequestArgs
        ): Promise<void> {
            const itemKey = instrumentPerformanceItemKeyFormatter(args);
            const dateFormatString = "DD-MMM-YY";
            useInstrumentPerformanceGraphSubStore().setValueLoaded({
                fetchKey: { username: args.username, partnerId: args.partnerId  },
                itemKey,
                value: [
                    { date: moment().format(dateFormatString), value: 19.99 },
                    { date: moment().subtract(3, "day").add(5, "hour").format(dateFormatString), value: 59.99 },
                    { date: moment().subtract(5, "day").add(8, "hour").format(dateFormatString), value: 31.99 },
                    { date: moment().subtract(7, "day").add(11, "hour").format(dateFormatString), value: 64.99 },
                    { date: moment().subtract(9, "day").add(15, "hour").format(dateFormatString), value: 44.99 },
                    { date: moment().subtract(11, "day").add(17, "hour").format(dateFormatString), value: 77.99 },
                    { date: moment().subtract(13, "day").add(19, "hour").format(dateFormatString), value: 108.99 },
                    { date: moment().subtract(15, "day").add(22, "hour").format(dateFormatString), value: 12.99 },
                ]
            });
        },
        async getInstrumentPerformanceData (
            { stockSymbol, username, partnerId, accountId }:
            {
                stockSymbol: string,
                username?: string,
                partnerId?: number,
                accountId?: number
            }
        ): Promise<void> {
            return useInstrumentPerformanceDataSubStore().fetchKeyedApiCall(
                { username, partnerId },
                stockSymbol,
                { stockSymbol, accountId }
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});